/**
 * 2026-06-07 — Help registry (comprehensive pass).
 *
 * Single source of truth for every inline help description shown in the
 * SPA. Components reference items by string id; the registry holds the
 * title + body text. The dispatcher (GM) is also given the titles +
 * one-line summaries via SYSTEM_PROMPT_IMPROV in dispatch.py so it can
 * elaborate on items when the player asks "what does X mean?" by voice
 * or chat.
 *
 * Categories (~125 items):
 *   stats      — characteristics, skill levels, status numbers
 *   rolls      — PC/EF/SC/QR/d100 mechanics
 *   navigation — paragraph + decision + drawer switching
 *   voice      — mic + read-aloud + voice-log
 *   ui         — toggles + drawer descriptions + buttons
 *   rules      — combat / wound / chase / persuasion / seduction tables
 *   skills     — 24 canonical skills (one entry each)
 *   npc        — allegiance, interaction modifiers, NpcCard fields
 *   gm-tools   — 20 dispatcher tool surfaces
 *
 * Help bodies use markdown-flavoured paragraphs separated by blank
 * lines — line breaks within paragraphs are preserved by the
 * HelpPopover renderer.
 */

export interface HelpItem {
  id: string;
  title: string;
  body: string;
  category?:
    | 'stats'
    | 'rolls'
    | 'navigation'
    | 'voice'
    | 'ui'
    | 'rules'
    | 'skills'
    | 'npc'
    | 'gm-tools';
}

const ITEMS: HelpItem[] = [
  // ═══════════════════════════════════════════════════════════════════
  // STATS / Character Numbers
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'primary-chance',
    title: 'Primary Chance (PC)',
    category: 'stats',
    body: "Primary Chance is the base number you must roll AT OR UNDER on d100 to succeed at an Ease Factor of 1. It's the foundation of every skill check.\n\nFormula: usually a Characteristic (or two averaged) plus the character's Skill Level. For example, Bond's Boating: [(PER 14 + DEX 12)/2] + Skill Level 12 = 25.\n\nSee the Stats drawer for the per-skill formula, your PC, and the substituted numbers.",
  },
  {
    id: 'skill-level',
    title: 'Skill Level (SL)',
    category: 'stats',
    body: 'Skill Level is the training rank you have in a skill: a 1-15 number. Higher = better. It feeds into Primary Chance.\n\nA Rookie typically has Skill Levels of 1-4; an Agent 4-10; a "00" agent 10-15.',
  },
  {
    id: 'strength',
    title: 'Strength (STR)',
    category: 'stats',
    body: 'Strength is one of five Characteristics (STR / DEX / WIL / PER / INT) rated 1-15. Drives carrying capacity, Hand-to-Hand damage, and many Hand-to-Hand / Mountaineering / Demolitions Primary Chances.',
  },
  {
    id: 'dexterity',
    title: 'Dexterity (DEX)',
    category: 'stats',
    body: 'Dexterity is the Characteristic rating reflexes + manual coordination. Feeds Driving, Piloting, Riding, Boating, Pickpocket, Stealth, Evasion, Fire Combat. Most physical skills sit on a DEX-based formula.',
  },
  {
    id: 'willpower',
    title: 'Willpower (WIL)',
    category: 'stats',
    body: 'Willpower is the Characteristic representing mental fortitude — your ability to resist interrogation, push through pain, or charm someone with sheer confidence. Feeds Persuasion, Seduction, Interrogation, Torture, and post-injury pain rolls.',
  },
  {
    id: 'perception',
    title: 'Perception (PER)',
    category: 'stats',
    body: 'Perception is the Characteristic for noticing, sensing, intuition. Feeds Sixth Sense, Fire Combat (long range), Cryptography, Lockpicking, Local Customs, and NPC recognition rolls.',
  },
  {
    id: 'intelligence',
    title: 'Intelligence (INT)',
    category: 'stats',
    body: 'Intelligence is the Characteristic for reasoning, memory, planning. Feeds Disguise, Electronics, Science, Demolitions, Photography, Cryptography. Many high-skill technical fields are INT-based.',
  },
  {
    id: 'fame-points',
    title: 'Fame Points',
    category: 'stats',
    body: "Fame Points measure your character's notoriety in the intelligence world. NPCs with sufficient Fame have a chance to recognize you on sight — high Fame is a double-edged sword: a great cover story is easier with Fame, but the opposition spots you faster.\n\nHigh-rank '00' agents start with high Fame; Rookies start near zero.",
  },
  {
    id: 'hero-points',
    title: 'Hero Points',
    category: 'stats',
    body: "Hero Points are spendable currency for re-rolls and clutch saves. Spend one to reroll a failed roll, to wake from unconsciousness, to bump a QR up a tier, to claim a QR1, or to invoke a heroic moment (e.g. realizing the tires of a sunken car still hold air).\n\nMost adventures grant 3-10 Hero Points to start; some adventures hand out bonus points for specific clever play.",
  },
  {
    id: 'survival-points',
    title: 'Survival Points',
    category: 'stats',
    body: "Survival Points are an NPC-only stat used by Major Villains + Privileged Henchmen to escape death in cinematic ways. When a villain is killed but has Survival Points remaining, the GM may rule they survived (and returns in a sequel). Player characters use Hero Points instead.",
  },
  {
    id: 'stamina',
    title: 'Stamina',
    category: 'stats',
    body: 'Stamina is the number of hours your character can stay active without rest. Once exhausted, Primary Chance suffers a penalty until you rest.',
  },
  {
    id: 'running-swimming',
    title: 'Running / Swimming',
    category: 'stats',
    body: 'The number of minutes you can run flat-out or swim before exhaustion. Important in chase + escape sequences (the Dr. No reactor escape, the Zorin lake-resort drowning trap, etc.).',
  },
  {
    id: 'carrying',
    title: 'Carrying Capacity',
    category: 'stats',
    body: 'How much you can carry comfortably, expressed as a pound range (e.g. 101-150 lbs). Above the range incurs combat + movement penalties.',
  },
  {
    id: 'speed',
    title: 'Speed',
    category: 'stats',
    body: 'Speed determines turn order in combat: higher Speed acts first. Speed 1 is average, Speed 3 is exceptional (May Day, Pola Ivanova).',
  },
  {
    id: 'character-rank',
    title: 'Character Rank',
    category: 'stats',
    body: "Rank reflects your standing in the service: Rookie → Agent → '00' → Mastermind. Higher rank = more starting Skill Points, Hero Points, and Fame.\n\nMost adventures are tuned for Agent or '00' rank; Rookies need GM help to clear them.",
  },
  {
    id: 'connoisseur',
    title: 'Connoisseur (ability)',
    category: 'stats',
    body: 'Connoisseur is a free ability — a Fields-of-Experience proxy for taste in food, wine, art, and rare collectibles. Used for cover stories at high society events.',
  },
  {
    id: 'first-aid',
    title: 'First Aid (ability)',
    category: 'stats',
    body: 'First Aid is a free ability — stabilize a Light or Medium wound, prevent it worsening before a doctor. Cannot reverse Heavy/Incapacitated.',
  },
  {
    id: 'photography',
    title: 'Photography (ability)',
    category: 'stats',
    body: 'Photography is a free ability — surveillance + dossier work. Used for the Signet Ring-Camera in A View to a Kill and similar gadgets.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // ROLLS / Dice Mechanics
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'ease-factor',
    title: 'Ease Factor (EF)',
    category: 'rolls',
    body: 'Ease Factor is the difficulty modifier the GM assigns to a roll. EF 1 = trivial (full PC), EF 5 = average (PC × 5), EF 10 = heroic (PC × 10). The half-EF (0.5) is "very hard" (PC ÷ 2, minimum 1).\n\nSuccess Chance = Primary Chance × Ease Factor.\n\nValid EFs: 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10.',
  },
  {
    id: 'success-chance',
    title: 'Success Chance (SC)',
    category: 'rolls',
    body: 'Success Chance is the actual d100 target you must roll at or under to succeed. SC = Primary Chance × Ease Factor.\n\nExample: PC 25 × EF 5 = SC 125 → you succeed on a d100 ≤ 99 (100 is always a failure).',
  },
  {
    id: 'quality-rating',
    title: 'Quality Rating (QR)',
    category: 'rolls',
    body: 'Quality Rating splits a successful roll into four tiers — QR 1 (Excellent), QR 2 (Very Good), QR 3 (Good), QR 4 (Acceptable). A d100 result of 100 is always a Failure.\n\nThe bracket boundaries are derived from the Success Chance: QR 1 ≈ SC/10, QR 2 ≈ SC/5, QR 3 ≈ SC/2, QR 4 ≈ SC (capped at 99).\n\nMany adventure paragraphs branch on the QR you achieved — a QR 1 may reveal more clues than a QR 3.',
  },
  {
    id: 'd100-roll',
    title: 'd100 roll',
    category: 'rolls',
    body: 'A d100 is two ten-sided dice combined (one for the tens digit, one for the units). Range 1-100. A roll of 100 is ALWAYS a failure regardless of Success Chance — there is no autopilot win.',
  },
  {
    id: 'percentile-roll',
    title: 'Percentile roll',
    category: 'rolls',
    body: 'A percentile roll is the d100 mechanic used for skill checks. The Other Rolls panel in the Roll drawer lets you fire a raw percentile when no skill is involved (e.g. a generic chance test).',
  },
  {
    id: 'generic-dice-roll',
    title: 'Generic dice roll',
    category: 'rolls',
    body: 'The Other Rolls panel handles four modes: Save (d20), Damage (e.g. 2d6+2), Skill Check (raw d100 with your choice of skill), Percentile (raw d100). None of these are tracked against your skill sheet; the GM uses them when the situation calls for a roll outside the canonical Bond resolution.',
  },
  {
    id: 'jam-range',
    title: 'Jam range',
    category: 'rolls',
    body: 'Each firearm has a Jam Range — a d100 roll on a Fire Combat check that falls in this range jams the weapon, requiring an action to clear. Example: Smith & Wesson .38 jams on 99; Tula Tokarev cold-jams on 97-99.',
  },
  {
    id: 'cold-jam',
    title: 'Cold-jam',
    category: 'rolls',
    body: 'Sub-zero environments (the Siberia opening of A View to a Kill, the Dr. No island at night) widen the jam range. Pistols cold-jam +1, machineguns cold-jam +3.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'paragraph-number',
    title: 'Paragraph navigation',
    category: 'navigation',
    body: 'Each paragraph is numbered. Decisions route you to other paragraphs by number. The History drawer shows the path you took; the GM Force-Paragraph control lets you jump to any number (escape hatch when the engine can\'t match your voice intent).',
  },
  {
    id: 'paragraph-page',
    title: 'Page reference',
    category: 'navigation',
    body: "The page number cited on each paragraph header refers to the source booklet (Core Rulebook for dr_no_adventure, VG35014 booklet for A View to a Kill, the 4Eyes PDFs for Diamonds Never Die + Code Red). Useful when you want to cross-reference the original printed source.",
  },
  {
    id: 'paragraph-decisions',
    title: 'Decision buttons',
    category: 'navigation',
    body: 'Each branching paragraph offers two to four lettered decisions (A / B / C / D). Click one to navigate; the History drawer records your choice. The GM voice can also dispatch a decision when you speak (e.g. "I take the elevator").',
  },
  {
    id: 'briefing-room',
    title: 'Back to Briefing Room',
    category: 'navigation',
    body: 'Returns you to the campaign picker without ending the current scenario state. Your scenario progress is saved per (campaign, character) — re-opening the same character in the same campaign resumes you exactly where you left off.',
  },
  {
    id: 'play-again',
    title: 'Play Again',
    category: 'navigation',
    body: 'Returns you to the character selector preserving the campaign — pick a different PC to try the same adventure with new stats. The scenario state for that (campaign, pregen) combination is cleared.',
  },
  {
    id: 'force-paragraph-input',
    title: 'GM Force-Paragraph',
    category: 'navigation',
    body: 'In the History drawer header: type any paragraph number + press Enter to jump directly. Escape hatch when the voice GM gets confused or you want to test a specific branch. Bypasses the decision graph entirely.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // UI / Toggles / Drawers
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'auto-roll',
    title: 'Auto-Roll mode',
    category: 'ui',
    body: 'When ON, the Quick-Roll button on each paragraph auto-fires once on mount — no click required. The dice cinematic still plays so you can see the result. Use this when you want a hands-off play experience.\n\nThis is an anytime toggle: switch it on or off whenever you like.',
  },
  {
    id: 'help-toggle',
    title: 'Help affordances',
    category: 'ui',
    body: 'When ON, small "?" icons appear next to UI elements, charts, and tables that have inline help text. Click one to read a definition.\n\nThe GM also knows these definitions and will elaborate if you ask by voice or chat (e.g. "what is a Quality Rating?").',
  },
  {
    id: 'stats-drawer',
    title: 'Stats drawer',
    category: 'ui',
    body: "Opens a panel with your character's full stat block — characteristics, skill levels with substituted formulas, weapons, and combat numbers. Each skill row shows you Primary Chance, Skill Level, and the formula in current numeric form so you can verify the math.",
  },
  {
    id: 'history-drawer',
    title: 'History drawer',
    category: 'ui',
    body: 'Shows the paragraphs you have visited in order, your rolls and their outcomes, and any generic dice rolls. The GM Force-Paragraph control at the top lets you jump to any paragraph by number — useful when the voice GM gets confused.',
  },
  {
    id: 'roll-drawer',
    title: 'Roll Dice drawer',
    category: 'ui',
    body: 'Manual roll panel: pick a skill, set an Ease Factor, and roll. The Roll drawer also includes Other Rolls — d20 saves, percentile checks, damage dice, free-form skill checks — none of which are tracked against your skill list.\n\nUse this when you want to roll something other than the paragraph\'s detected trigger.',
  },
  {
    id: 'audio-drawer',
    title: 'Audio drawer',
    category: 'ui',
    body: 'Per-tenant background music + ambient sound controls. Master volume slider (gates all in-app audio including voice + Read Aloud). Theme music + ambient loops + SFX (gun-barrel sting, dice cinematic, fame-recognition chime) all route here.',
  },
  {
    id: 'voice-log-drawer',
    title: 'Voice Log drawer',
    category: 'ui',
    body: 'Transcript history of your spoken inputs + the GM\'s replies. Useful when you missed something the GM said, or want to see exactly which intent the dispatcher picked up.',
  },
  {
    id: 'npcs-drawer',
    title: 'NPCs drawer',
    category: 'ui',
    body: 'Lists every NPC the player has encountered in this campaign, with full stat blocks, interaction modifiers, and recognition state (encountered / recognized / unknown). Click an NPC for the deep card.',
  },
  {
    id: 'pdf-library',
    title: 'PDF Library',
    category: 'ui',
    body: 'Access to the source PDFs (Core Rulebook, scenario booklets, Q Manual, supplements) bundled with the briefing-room module. Useful for the GM to check a rule mid-session.',
  },
  {
    id: 'modules-library',
    title: 'Module Library',
    category: 'ui',
    body: 'Catalog of all installed adventure modules. Click a module to expand its NPC roster, locations, and mission sheets. The "▶ Play this scenario" button switches the active adventure (the page will reload to reset the scenario state).',
  },
  {
    id: 'equipment-library',
    title: 'Equipment Library',
    category: 'ui',
    body: 'Canonical Q Manual VG35002 weapons catalog — 93 entries across Pistols, Rifles/SMG/Shotguns, Miscellaneous Modern Weapons, Accessories, and Archaic Weapons. Click any row to expand its stat block (PM / S/R / AMMO / DC / CLOSE / LONG / CON / JAM / DRAW / RL / COST) + the Q evaluation prose + game-mechanics rules. Each stat column has its own help icon explaining the rule.\n\nVehicles, gadgets, drugs, and Q Branch personnel will be added in subsequent phases.',
  },
  {
    id: 'vehicles-library',
    title: 'Vehicles Library',
    category: 'ui',
    body: 'Canonical Q Manual VG35002 vehicles catalog — 92 entries across Land (58), Air (20), and Water (14). Each tab groups entries by subcategory (e.g. automobile vs miscellaneous_land; jet_aircraft vs rotary_aircraft; surface_boat vs underwater_craft). The 4th tab shows the 31 Vehicle Modifications (Q-Branch mod-budget math: total mod-Points ≤ host vehicle\'s STR).\n\nClick any row to expand its stat block (PM / RED / CRUS / MAX / RGE / FCE / STR / COST) + the Q evaluation prose + game-mechanics rules. Each stat column has its own help icon explaining the rule.',
  },
  {
    id: 'gadgets-library',
    title: 'Gadgets Library',
    category: 'ui',
    body: 'Canonical Q Manual VG35002 gadgets catalog — 59 entries across 13 fine-grained categories that cluster into 3 super-categories (drawer tabs):\n\n• Common Devices (26) — attache cases, belts, pens, shoes, umbrellas, watches, miscellaneous common devices. Look like ordinary objects but have alternate uses.\n\n• Security Devices (22) — search/identification (fluoroscope, retina scan, palmscan…), surveillance bugs, counter-surveillance receivers, sensors (acoustic, chemical, ground radar, seismic), and tracers (Avram, Davey, Echo, Scarab).\n\n• Exotic Devices (11) — equipment "noticeably different by their very appearance" or villain-tier personal weaponry. Click any row to expand its description + Q evaluation + game-mechanics rules + per-rule special_rules block.',
  },
  {
    id: 'drugs-library',
    title: 'Drugs & Poisons Library',
    category: 'ui',
    body: 'Canonical Q Manual VG35002 drugs + poisons catalog — 21 entries cleanly split into 2 categories (drawer tabs):\n\n• Drugs (10) — amphetamines, general anesthetics (chloroform, halothane, thiopental sodium), atropine, haloperidol, morphine, sodium amytal, scopolamine. Mostly anesthetic + tranquilizing agents used as incapacitants or in interrogation contexts.\n\n• Poisons (11) — black widow venom, cyanide, nerve gas, Delta Nine, gamma gas, orchidea negra, snake hemotoxins, neurotoxins, tarantulas. Lethal agents typically used by villains.\n\nClick any row to expand its description + Q evaluation + game-mechanics rules. Schema quirk: source seed file is drugs.json but entries-array key is "agents" (top_level_key surfaced in manifest for parser awareness).',
  },
  {
    id: 'q-branch-personnel-library',
    title: 'Q Branch Personnel',
    category: 'ui',
    body: 'Canonical Q Manual VG35002 §18 dossiers — 6 NPCs covering the entire Q Branch staff during the 007 era. The drawer organizes them by their canonical Q Manual factional structure (3 tabs):\n\n• Leadership (1) — Major Geoffrey Boothroyd (Q), head of department since 1951. INT 15, Science 15/30, Cryptography 11/26, Electronics 12/27.\n\n• Harrow Club (4) — Dr. Walter Cobbett (vehicle modifications, joined 1967), Jonathan Maclean (cryptography + electronics, 1968), Dr. Lawrence Powell (aerospace + economics, 1973), Captain Michael Roberts (operations + field-test, 1976). Founding members of an informal Q Branch faction that meets every 3 weeks for bridge/whist.\n\n• Newer Member (1) — Dr. Elaine Turner (forensic chemist, joined 1982). Explicitly characterized in the source as a recent addition not part of the Harrow Club.\n\nClick any row to expand the full dossier: stats (STR/DEX/WIL/PER/INT), skills with skill_level + primary_chance, abilities, physical description, Fame + Hero points, fields of experience, background + personality + relationships prose blocks, plus GM-facing game-information notes.',
  },
  {
    id: 'npc-archetypes-library',
    title: 'NPC Archetypes',
    category: 'ui',
    body: 'Canonical Core Rulebook §28 NPC archetype generator tables — 8 archetypes the GM rolls 2d6 against to generate stats + skills for any NPC the players meet. The drawer organizes them by narrative role (3 tabs):\n\n• Allies (2) — Beautiful Foil (romantic interest + ally; Honeychile Ryder / Tatiana Romanova / Tiffany Case / etc.) + Fellow Secret Agent (other 00-rank or Mastermind-rank operatives; Felix Leiter / Kerim Bey / Tiger Tanaka).\n\n• Enemies (3) — Major Villains (Goldfinger / Blofeld / Dr No), Privileged Henchmen (Oddjob / Jaws / Nick Nack), Guards and Soldiers (Mastermind\'s rank-and-file).\n\n• Neutrals (3) — Civilians (background non-combatants), Shady Contacts (criminal underworld informants with 9 named subtypes Bond may rendezvous with), Technicians (mechanics, electronics specialists, weapons-makers, etc).\n\nEach archetype has a per-rank stat modifier scale (Hood/Rookie/default/Mastermind), a 2d6 characteristics table generating STR/DEX/WIL/PER/INT, and a 2d6 skills table picking the NPC\'s skill kit. Click any row for the description + canonical examples + rank modifiers; the full 2d6 tables live in the seed file.',
  },
  {
    id: 'villains-dossier-library',
    title: 'Villains Dossier (VG35011)',
    category: 'ui',
    body: 'Canonical Villains Supplement (Victory Games VG35011) — 26 named NPCs across the Major Villains booklet + SMERSH booklet. The drawer organizes them by org-role (3 tabs):\n\n• Major Villains (7) — Cartesia (technocratic; controls scientific research via global Thinker network + underwater fortress with hyperbaric mechanics), Etienne Buonvisi (Renaissance-style economic empire builder; Legion S.A. + chess minigame mechanics + violence-aversion policy), Feolina Vandemere (biochemical love-induction mercenary; ex-CIA seductress avenging her father), Lyndon Parkhurst ("The Ripper"; sociopathic for-hire assassin, Texas oil heir cover, trick walking sticks), Franz Kleiner (Neo-Nazi Werewolf Force leader; 5-Division covert org pursuing Fourth Reich via infiltration), Octavia Prosinski (Polish-born terrorist mercenary; ex-Red Brigade pure-profit operator with Skorpion VZ 61), Pierre Larose (ex-Canadian politician turned pacifist-terrorist; "destroy to save" psychology; 2 lieutenants).\n\n• Lieutenants (9) — 3 Cartesia Thinkers (Jean Delorme Silicon Valley senior designer + Jacques Bartheleme Paris political cartoonist National Thinker for France + Shara Devi honey-trap specialist), 2 Vandemere prose-only (Dr. Dorothy "Dot" Van Esterfeld + Shana Golder; named in §Organization prose but NO stat block in source MD), 2 Octavia lieutenants (Helga Egermann ex-Baader-Meinhof + Curtis Senegal South African ex-ZANU mercenary), 2 Larose lieutenants (Dr. Olga Kapinski U Montreal Political Science professor + Robert Olson radical journalist). Prose-only entries render with a ⚠ banner indicating null stat fields + role/background only.\n\n• SMERSH (10) — 4 leaders using simplified WIL+PER stat blocks (Nikolai Lebedev "00"-rank knife-killer Planning & Administration head + Yuri Shadrin Intelligence head blackmailed by Orlov + Alexander Kreniak Operations head apolitical strategist + Dr. Ulrich Johann Skoda Research/Murder Factory head ex-Hitler-Youth weapons designer) + 6 agents using full 5-stat blocks (Peter Fedisov OFFICIAL active patriotic killer + Prussic Acid Sprayers; George Pimberton OFFICIAL active Oxford defector + Browning 1922; Melody Valentine retainer Brooklyn seductress + C.O.P. .357; Dieter Morgen retainer methodical professional + H&K P7 M13; Sureyov Malak retainer Turkish bastinoe torture specialist + Turkish MKE; Castenada retainer Basque ETA demolitions specialist + Llama VIII).\n\n§3.B (commit 3f5b1f205) + §3.B.2 (093ca6126) + §3.C (88318d27d) authored 26/29 entries; pending = 3 Branded Man adventure NPCs (Buhardin / Williams / Corlin) deferred to §3.J alongside the 5th CYOA scenario. §3.C also surfaced a 4th SMERSH leader (Shadrin) missed by the planning-doc enumeration because his stat block at MD line 2620 lacks a `#### NAME` header — honest correction documented in seed.',
  },
  {
    id: 'thrilling-locations-npcs-library',
    title: 'Venue Staff (VG35010)',
    category: 'ui',
    body: 'Canonical Thrilling Locations (Victory Games VG35010) — 39 named venue-staff NPCs, organized by the 6 venue tabs:\n\n• Casino (7) — owners (Mako Singoshu, Marcos "The Shark" St. John, Aleeta Chartoncyr, Marty "The Martinet" Stephens) + managers (Heinrich Beck, Madeline Peltier, Manuel Armendariz).\n• Hotel (6) — managers (Jones, Felicia Delacroix, Maximillian Reisler) + desk clerks (Li-Sin Morceauloin, Marsac, Lars Nilgren).\n• Restaurant (4) — owner/managers (Rider Stevenson, Carla Simone, Cory Peterson) + headwaiter (O\'Banyon).\n• Train (7) — conductors (Asaada, Carlson J. Claghorn, Porterfield) + security officers (Carlotta Stanchini, Despar, Shimada) + entertainer (Fred Loverly).\n• Boat (7) — owners (O\'Shay, Lord MacPherson, Helena Skouropulous) + captains (Geoffrey Tyson, Charlie Rataze, Benjamin Stafford) + engineer (Jane Topbett).\n• Plane (8) — pilots (Willoughby, Roger Montana, Jessica Winston) + flight attendants (Sabrina Delandre, Manuel Garcia Vega y Perez, Judy Lynn Walker) + ticket agents (Tony Sparanza, Burns).\n\nThese are venue staff: the source gives a PARTIAL stat block (WIL/PER per the Civilians Table; STR/DEX/INT render "—") plus physical, weaknesses, idiosyncrasies, interaction modifiers (STRING values so dual notation "0/+2" survives), concept + verbatim background. Four NPCs (O\'Shay, Willoughby, Asaada, Stanchini) lost their stat header to marker OCR and surface a ⚠ note. Two surname coincidences were cleared in the §3.A audit: Felicia Delacroix is NOT Bond\'s mother Monique; Lars Nilgren is NOT the Branded Man "nilgren" red-herring stub. Casino/hotel/restaurant/train/boat/plane VENUES (with bet-limits + GM guidance) live in the Thrilling Cities seed; gambling resolves via the canonical Ch 7 engine.',
  },
  {
    id: 'survival-points',
    title: 'Survival Points (Villains-only)',
    category: 'rules',
    body: 'Villains-supplement equivalent of the PC-side Hero Points mechanic. Where Hero Points are spent by player characters to declare narrative escapes / re-rolls / cinematic interventions, Survival Points are the villain\'s analog — a pool the GM may spend to declare the villain escaped certain death, retreated from a captured situation, or arranged a contingency the players didn\'t anticipate.\n\nMajor Villain Survival Points range 5-14 (Buonvisi has the highest at 14, reflecting his anti-violence careful planning; Kleiner the lowest at 5, reflecting his open Werewolf-Force exposure). Lieutenants typically 3-5; SMERSH agents 4-12 with Morgen at 12 reflecting his 35-year career-survival record.\n\nIn the Villains Dossier drawer, the Survival tile appears next to the Fame tile in the points section.',
  },
  {
    id: 'smersh-directorate',
    title: 'SMERSH Directorate',
    category: 'rules',
    body: 'The 4 official directorates of the revived SMERSH per Politburo charter (per Villains Supplement §1.2 SMERSH booklet line 2560):\n\n• Planning and Administration (Nikolai Lebedev) — runs SMERSH operationally + maintains the bogus reports submitted to the Politburo + compiles the Enemies of the State list.\n• Intelligence (Yuri Shadrin) — covert KGB-files access through Shadrin\'s blackmail-coerced computer link; gathers personal data including blackmail material on Soviet leaders + KGB agents.\n• Operations (Alexander Kreniak) — plans + coordinates all SMERSH operations. Pre-report style: files plan + expected reactions + outcomes BEFORE the mission.\n• Research (Dr. Ulrich Johann Skoda) — euphemism for the old Murder Factory. Designs (and builds) new weapons for assassination, with Skoda\'s specialty in disguising weapons in innocuous objects.\n\nThe charter says 4 directorates; in practice the entire operation reports to Vladimir Orlov (the hidden architect; not himself a directorate head). Lebedev + Kreniak are Orlov\'s allies-by-shared-ideology; Shadrin is Orlov\'s blackmail victim; Skoda is apolitical pure-craftsman.',
  },
  {
    id: 'smersh-official-active',
    title: 'SMERSH Official Active vs Retainer',
    category: 'rules',
    body: 'The Politburo capped the size of the revived SMERSH at 3 officially-active agents on record. Orlov compensated by maintaining a retainer-payroll of outside-recruited assassins ("on retainer" so they\'re ready at any time to carry out missions for SMERSH).\n\n• OFFICIAL ACTIVE (3 named) — Peter Fedisov, George Pimberton, and Natasha Buhardin (Buhardin\'s dossier is in the Branded Man adventure section, deferred to §3.J).\n\n• RETAINER (4 of 6 in §1.2 booklet) — Melody Valentine, Dieter Morgen, Sureyov Malak, Castenada. Not on Politburo records; recruited from the global freelance-assassin pool. Some have ideological commitment to SMERSH (Valentine grew to enjoy it; Fedisov is patriotic), but several are pure mercenaries (Morgen forced out of retirement by his daughter\'s medical bills; Malak supporting his family back in Afyon).\n\nThe Villains Dossier drawer surfaces a Status badge for each SMERSH agent indicating Official vs Retainer.',
  },
  {
    id: 'modus-operandi',
    title: 'Modus Operandi (MO)',
    category: 'rules',
    body: 'Signature method-of-operating field per SMERSH agent dossier (every agent in the Villains Supplement §1.2 booklet has a `#### MODUS OPERANDI` block following their stat block). Captures HOW the agent handles assignments and the general flavor of adventures involving that NPC.\n\nExamples in the catalog:\n• Fedisov: simple bedroom-shooting; lacks imagination; can\'t improvise.\n• Pimberton: theatrical accident-staged kills; works only outside his British safe haven.\n• Valentine: seduction-based; close-range C.O.P. .357 emptied during a hug or kiss.\n• Morgen: meticulous research-driven; studies a target for months before moving.\n• Malak: simple/neat/straightforward; tail-to-isolation, knock out, break neck, dispose; bastinoe specialist.\n• Castenada: demolitions; thinks nothing of wholesale destruction to kill his target.\n\nWhen running a scenario with one of these agents, the MO determines what kind of player-character investigation will surface them — and what kind of trap they\'ll have laid.',
  },
  {
    id: 'fame-points-public-cover',
    title: 'Fame Points / Public-Cover Variant',
    category: 'rules',
    body: 'Some Villains-supplement NPCs maintain a public-figure cover with a SEPARATE Fame Points value than their covert/agent identity. Recognition rolls vs the covert identity use the lower Fame value; recognition vs the public cover uses the higher one.\n\nExamples in the Villains catalog:\n• Jacques Bartheleme (Cartesia\'s National Thinker for France): Fame Points 34 as Cartesia agent / 140 as political cartoonist (his Paris cartooning column has wide European reach).\n• Peter Fedisov (SMERSH official-active assassin): Fame Points 65 as SMERSH covert / 120 as patriotic Ukrainian farm-boy soldier.\n\nThe Villains Dossier drawer renders these as `<covert>/<public>` (e.g. "34/140") in the Fame tile. NPCs without a dual cover render just the single Fame value.',
  },
  {
    id: 'major-villain-lieutenant',
    title: 'Major Villain Lieutenant',
    category: 'rules',
    body: 'Per-Major-Villain confidant + operational lieutenant. The Villains Supplement enumerates 7 stat-block lieutenants (3 for Cartesia, 2 each for Octavia + Larose) and 2 prose-only lieutenants (both Vandemere\'s organizational section heads). NO named individual lieutenants for Buonvisi (whose Legion S.A. is described corporately) or Parkhurst (who works alone with disposable accomplices) or Kleiner (whose 5-Division Werewolf Force is described structurally without naming individuals).\n\nIn the Villains Dossier drawer, lieutenants appear in the Lieutenants tab with a `Serves Under` callout linking to their Major Villain ID. Prose-only lieutenants get a ⚠ banner indicating no stat block in source MD.\n\nAuthored §3.B.2 (commit 093ca6126).',
  },
  {
    id: 'rank-note',
    title: 'Rank Note (SMERSH Leaders)',
    category: 'rules',
    body: 'SMERSH leaders use a SIMPLIFIED 2-stat stat block (WIL + PER only) per the source MD body-text instructions:\n\n• Lebedev: "Lebedev should be of \'00\' rank" — use Core Rulebook 00-rank stat distribution for STR/DEX/INT and any skill levels not enumerated.\n• Shadrin: "Shadrin should be an ordinary citizen, and have appropriate characteristics and skills" — Civilian rank.\n• Kreniak: "Kreniak should be of Rookie rank, but with a very high Intelligence and Perception" — Rookie rank with INT/PER bumps.\n• Skoda: "Skoda has Civilian characteristics, but with a high Intelligence and high Skill Levels in Science and Electronics" — Civilian with specialist Science/Electronics bumps.\n\nThe Villains Dossier drawer renders this guidance as an ℹ "Rank note" callout for each leader, and shows STR/DEX/INT as `—` (em-dash) instead of 0 to make explicit that the value is rank-derived rather than provided.\n\nThis stat-block shape distinguishes SMERSH leaders from SMERSH agents (who use the standard 5-stat block).',
  },
  {
    id: 'canonical-npcs-library',
    title: 'Canonical NPCs (Ch 18)',
    category: 'ui',
    body: 'Canonical Core Rulebook Chapter 18 "Allies and Enemies of James Bond" + 4 see-Chapter-1 cross-references — 34 named characters from the Bond canon with full stat blocks (STR/DEX/WIL/PER/INT, skills, abilities, Fame Points, Hero Points for allies / Survival Points for enemies, Fields of Experience, Weaknesses, background prose). The drawer organizes them by allegiance (3 tabs):\n\n• Allies (11) — Kerim Bey (Station T head, MI6 Turkey), Tracy Bond (née Countessa Teresa di Vicenzo), Tiffany Case (Diamonds Are Forever), Milos "Dove" Colombo (Aegean smuggler ally), Marc Ange Draco (Union Corse, Tracy\'s father), William Fairbanks ("009", Bond\'s closest friend in service), Sheriff J.W. Pepper (Louisiana stereotype), Quarrel (Bahamas fisherman), Honeychile Ryder (Bahamas naturalist), Solitaire (psychic/predictor), Tiger Tanaka (head of Japanese Secret Service).\n\n• Enemies (19) — Sir Hugo Drax, Pussy Galore (defected per canon), Auric Goldfinger, Donovan "Red" Grant (SMERSH chief executioner), Jaws (book-known STR=18 implausibility tag), Kananga (San Monique PM), Mr. Big (Kananga\'s alter-ego; uses Kananga\'s stats per source NOTE), Rosa Klebb (ex-SMERSH), Aristotle Kristatos (Greek smuggler), Alexei Kronsteen (TAROT planning director), Emil Leopold Locque, Dr. Julius No (bionic-handed nuclear physicist), Oddjob (Goldfinger\'s bodyguard, throws razor-edged derby), Tetsuro Osato (TAROT money laundering), Francisco "Paco Pistols" Scaramanga (one million per kill), Karl Stromberg (oceans-not-space industrialist), Tee Hee (Kananga\'s pincer-armed bodyguard), Mr. Wint + Mr. Kidd (paired hitmen).\n\n• See Ch 1 (4) — Stats pulled in from Chapter 1: Felix Leiter (CIA), Holly Goodhead (NASA agent), Mary Goodnight (Rookie), Lt. Chong Sun Hip (Hong Kong liaison).\n\nF3-CONT (2026-06-08, same session as F3-FIX) closed the authoring gap: F3-FIX shipped 6 of 28 with a honest banner; F3-CONT authored the remaining 22 + 4 see-Ch1 stubs so pending_remaining_count is now 0. Notable artifacts preserved verbatim per book-faithful policy: Jaws STR=18 (source\'s own NOTE acknowledges; ⚠ chip in detail); OCR-truncated STR fields on a few entries (Tracy Bond / Felix Leiter / Sir Hugo Drax / Kristatos) preserved as absent rather than guessed.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // Q MANUAL CONCEPT GLOSSARY (Phase 1-5 integration helpers — 2026-06-08)
  // Concepts referenced in the new Vehicles/Gadgets/Drugs/Q Branch drawers
  // that benefit from short standalone explainers.
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'hand-to-hand-damage-class',
    title: 'Hand-to-Hand Damage Class',
    category: 'rules',
    body: "The Damage Class a character inflicts in unarmed combat — distinct from weapon DC. Letter scale: A (weakest) < B < C ... The class is calculated from STR and modified by Hand-to-Hand Combat skill level.\n\nIn the Q Branch personnel dossiers, all the scientists (Boothroyd, Turner, Cobbett, Maclean) have H2H DC A — they are not fighters. Powell and Roberts both have B — Roberts is a former 3rd Commando + M.I.6 field agent; Powell is the strongest scientist (STR 9).\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'fields-of-experience',
    title: 'Fields of Experience (FoE)',
    category: 'rules',
    body: "Optional rule from Q Manual + Core Rulebook §2: declared narrow areas of expert knowledge — e.g. Cricket, Toxicology, Computers, Space Sciences, International Law. When a roll involves a FoE the character has, the GM may grant an EF bonus or auto-success for trivial applications.\n\nFoE are descriptive, not mechanical — they encode the character's biography in a usable form. Roberts has 11 FoE (broadest education); Cobbett has 5 (focused on engineering + games).\n\nSource: Core Rulebook §2 + each dossier's Fields of Experience field.",
  },
  {
    id: 'appearance-rating',
    title: 'Appearance Rating',
    category: 'rules',
    body: "5-level ordinal scale for character physical appearance: Repulsive < Unattractive < Normal < Attractive < Striking < Sensational.\n\nUsed by Seduction skill rolls (target rolls WIL save vs an Ease Factor influenced by the seducer's appearance) and by NPC-recognition Charisma rolls.\n\nIn Q Branch dossiers: Boothroyd Normal, Powell Normal, Cobbett Unattractive, Maclean Attractive, Turner Striking, Roberts Sensational. Roberts' Sensational + 88 Fame Points explains why his presence in a scenario draws notice.\n\nSource: Q Manual + Core Rulebook character generation.",
  },
  {
    id: 'harrow-club',
    title: 'Harrow Club (Q Branch faction)',
    category: 'rules',
    body: "Informal Q Branch faction — 4 members (Cobbett, Maclean, Powell, Roberts) who attended Harrow School together. Formed during their school days and kept in contact ever since.\n\nMeets every three weeks for bridge (or whist if Cobbett has his way). Before a game they have a tradition called 'showing the grumbles' — a holdover from the days of complaints about schoolmasters. The member with the most superior grumble that session is given sympathetic applause + the privilege of providing beverages for the next meeting.\n\nThe faction is descriptive — there is no mechanical bonus for being a member. But it is significant for narrative reasons: Dr. Turner's deliberate non-membership characterizes her as deliberately maintaining social distance from the older Q Branch members. Captain Roberts' romantic advances on Dr. Turner + Cobbett's similar (gently rejected) interest are surfaces of Harrow-Club-vs-newer-member dynamics worth leveraging in scenarios.\n\nSource: Q Manual §18 (MD L4147-L4155).",
  },
  {
    id: 'structure-points-budget',
    title: 'Structure Points Mod-Budget',
    category: 'rules',
    body: "Core rule from Q Manual §12 (Vehicle Modifications, p.83): a vehicle's STR rating is also its modification budget. A vehicle may carry modifications totaling no more than its STR in Structure Points.\n\nExamples:\n  • Lotus Esprit (STR 5) → max 5 Structure Points of mods\n  • Aston Martin DB-V (STR 9) → max 9 Structure Points\n  • C-141B Starlifter (STR 230) → effectively unlimited mod capacity\n\nModifications without an explicit Structure Point cost default to ½ a Structure Point (rule-of-thumb at Q Manual p.83). Armor mods (Levels I-IV) use a special per-STR-point pricing model + cost in GBP, multiplied by host vehicle's original STR.\n\nSource: Q Manual §12 page 83.",
  },
  {
    id: 'gadget-stats-schema',
    title: 'Gadget stats_schema field',
    category: 'rules',
    body: "Unlike weapons + vehicles (which share a uniform stat block), Q Manual gadgets carry heterogeneous stats — different gadget classes use different mechanical schemas. The seed file's `stats_schema` field declares which one applies per entry:\n\n• `non_combat` — narrative-only device (e.g. Attache Case MS67g, CEB 1000 attache); no stat block\n• `armor_modifier` — modifies host equipment armor (e.g. Attache Security Linings)\n• `weapon_stats` — uses the weapon DC/range/etc. schema (e.g. Glove Gun, Umbrella Pistol)\n• `tracer` — Avram, Davey, Echo, Scarab — uses range + battery-life schema\n\nThis is why the Gadgets drawer detail block focuses on prose (description + Q evaluation + game info + contents list) rather than rendering a uniform stat grid.",
  },
  {
    id: 'atmosphere-drawer',
    title: 'Atmosphere drawer',
    category: 'ui',
    body: 'Per-tenant atmospheric settings — desk background, voice picker, narration mode (improv vs by-the-book), mic activation mode (hold vs toggle), voice debug overlay.',
  },
  {
    id: 'theme-drawer',
    title: 'Theme drawer',
    category: 'ui',
    body: 'Select one of 6 visual themes: Dossier Noir (default), Cold Steel, Casino Royale, Bonded Brief, UK Monarchy, US/UK Flag. The selection persists across reloads.',
  },
  {
    id: 'quick-roll-button',
    title: 'Quick Roll button',
    category: 'ui',
    body: 'When a paragraph mentions a specific skill + Ease Factor, this button pre-fills the roll for one-click resolution. Disabled if you do not have the required skill — use the Roll drawer manually in that case.',
  },
  {
    id: 'pick-roll-button',
    title: 'Pick Roll button',
    category: 'ui',
    body: 'Opens the Roll Dice drawer pre-focused on the skill picker. Useful when a paragraph mentions a roll but doesn\'t specify which skill or you want to pick an alternative.',
  },
  {
    id: 'ask-gm-button',
    title: 'Ask GM button',
    category: 'ui',
    body: 'Fires a voice-prompt to the GM dispatcher asking which roll fits the current paragraph. The GM replies with a recommended skill + EF; you can then accept or override.',
  },
  {
    id: 'hero-image',
    title: 'Hero image',
    category: 'ui',
    body: 'Per-paragraph scenic image rendered above the paragraph text. dr_no_adventure has 6 mapped scenes (Tobruk harbor, the island, Ben Gazi office, cell block, control room, underground missile facility). Other scenarios render text-only until per-scenario scene maps are authored.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // VOICE / Dispatcher
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'mic-control',
    title: 'Voice GM',
    category: 'voice',
    body: 'Press and hold the microphone, speak your intent ("I check the corridor for guards"), release. The GM dispatcher parses your voice into one of several tools — pick decision, roll skill, describe NPC, look around, check inventory, ask NPC.\n\nThe GM will speak back via the Read-Aloud control. Use the Voice Log drawer to see the transcript history.',
  },
  {
    id: 'read-aloud',
    title: 'Read Aloud',
    category: 'voice',
    body: 'Speaks the current paragraph text or GM reply through your system\'s text-to-speech. Play, Pause, and Stop buttons behave as labeled — Pause stays paused until you Resume.',
  },
  {
    id: 'voice-debug',
    title: 'Voice Debug overlay',
    category: 'voice',
    body: 'Fixed bottom-right textarea + Send button that calls the same transcript handler as the mic. Useful for Playwright testing + debugging dispatcher intent mapping when mic permission is brittle.',
  },
  {
    id: 'narration-mode',
    title: 'Narration mode',
    category: 'voice',
    body: 'Switches the GM personality between two presets: Improv (loose, fast, generous interpretation of player intent) and By the Book (strict, asks for clarification when ambiguous, sticks to dispatched outcomes only).',
  },
  {
    id: 'voice-picker',
    title: 'Voice picker',
    category: 'voice',
    body: 'Choose the GM\'s text-to-speech voice from your system\'s available voices. Different OS / browser will have different voices available.',
  },
  {
    id: 'mic-activation',
    title: 'Mic activation mode',
    category: 'voice',
    body: 'Two modes: Push-To-Talk (hold the mic button while speaking, release when done) or Click-To-Toggle (click to start, click again to stop). Default is Push-To-Talk.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // RULES (charts, tables, mechanics)
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'qr-brackets-table',
    title: 'QR brackets table',
    category: 'rules',
    body: "The bracket table inside the Roll drawer shows the four QR ranges + the Failure range for a given Success Chance. Each tile lists the inclusive d100 range and the % chance of landing there.\n\nUse this before committing to a roll: a low SC means most outcomes fall in QR 3-4 or Failure; a high SC pushes more results into QR 1-2.",
  },
  {
    id: 'multiplication-table',
    title: 'Multiplication table (SC lookup)',
    category: 'rules',
    body: 'The canonical Core Rulebook p.158 table giving Success Chance = PC × EF for every PC 1-30 and EF 0.5-10. The engine computes the formula-based fast path; the JSON table is the ground-truth pin.',
  },
  {
    id: 'quality-results-table',
    title: 'Quality Results table',
    category: 'rules',
    body: 'Core Rulebook p.157 — maps d100 roll + Success Chance to a Quality Rating (1/2/3/4) or Failure. The engine computes brackets from rangeUpper = ceil(SC/10)*10; QR 1 = roll ≤ rangeUpper/10, etc.',
  },
  {
    id: 'persuasion-chart',
    title: 'Persuasion / Seduction chart',
    category: 'rules',
    body: "When you persuade or seduce an NPC, the result is compared against their Willpower with the NPC's interaction modifiers applied. A '?' result means the GM chooses whether the NPC believes you; a 'Y' means yes; an 'N' means no. See Chapter 6 of the Core Rulebook.",
  },
  {
    id: 'persuasion-modifiers',
    title: 'Persuasion EF modifiers',
    category: 'rules',
    body: 'Five canonical modifier classes adjust the Persuasion EF: trivial_request (+1), minor_request (0), major_request (-1), deadly_request (-3), appropriate_emotion (+1 bonus if you display the right feeling).',
  },
  {
    id: 'seduction-five-stages',
    title: 'Seduction five stages',
    category: 'rules',
    body: 'Seduction is a five-stage sequence with canonical EFs 10 → 9 → 8 → 5 → 4. (1) Opening overture, (2) Witty conversation, (3) Gaining trust, (4) Begin intimacies, (5) When and where. Each stage must succeed to advance; failure on any breaks the sequence.',
  },
  {
    id: 'seduction-appearance-modifier',
    title: 'Appearance modifier',
    category: 'rules',
    // 2026-06-08 Fix #E — entry rewritten to match the authoritative
    // seed table (seed/tables/seduction_ef_modifiers.json). Pre-fix the
    // body inverted subject (NPC vs PC), inverted signs, and named a
    // "Repulsive" class that has never existed in the table.
    body: "Your Player Character's appearance adjusts seduction EF cumulatively with situational modifiers: Plain (-3), Normal (-1), Good Looking (0), Attractive (+1), Striking (+2), Sensational (+4). Lower EF = harder; higher EF = easier. Bond pregens default Striking / Sensational. Source: Core Rulebook Ch 6 p.64.",
  },
  {
    id: 'combat-rules',
    title: 'Combat — Fire & Hand-to-Hand',
    category: 'rules',
    body: 'Fire Combat: roll against your Fire Combat skill at the Ease Factor for the range bracket of the target. Damage Class depends on the weapon. Hand-to-Hand: roll against Hand-to-Hand Combat at appropriate EF; damage is based on Speed + Damage Class (A/B/C).\n\nSee Chapter 4 of the Core Rulebook for full combat resolution including Speed adjustments for drawing weapons.',
  },
  {
    id: 'damage-class-table',
    title: 'Damage Class table',
    category: 'rules',
    body: 'Damage Classes A through L (12 levels) map weapons + attacks to wound outcomes. DC A is a slap; DC F is a pistol round; DC L is a high-explosive direct hit.',
  },
  {
    id: 'wound-accumulation',
    title: 'Wound accumulation',
    category: 'rules',
    body: 'Wounds accumulate: Stunned (recoverable in 1 round) → Light Wound → Medium Wound → Heavy Wound → Incapacitated → Killed. Each successive wound makes you more vulnerable.',
  },
  {
    id: 'light-wound',
    title: 'Light Wound (LW)',
    category: 'rules',
    body: 'Minor injury — a flesh wound or scrape. -1 EF penalty to all physical actions until treated (First Aid stabilizes, doctor heals).',
  },
  {
    id: 'medium-wound',
    title: 'Medium Wound (MW)',
    category: 'rules',
    body: 'Significant injury — broken rib, deep cut. -2 EF penalty + Pain Resistance roll to keep going. Bleeds without treatment.',
  },
  {
    id: 'heavy-wound',
    title: 'Heavy Wound (HW)',
    category: 'rules',
    body: 'Critical injury — punctured lung, severed artery. -3 EF + Pain Resistance roll every round. Without immediate medical help: progresses to Incapacitated then death.',
  },
  {
    id: 'incapacitated',
    title: 'Incapacitated (IN)',
    category: 'rules',
    body: 'Out of the fight — unconscious or unable to act. Three failed Pain Resistance rolls in a row at this level: death. Spend a Hero Point to wake up.',
  },
  {
    id: 'killed',
    title: 'Killed (KL)',
    category: 'rules',
    body: 'Dead. End of character (unless they have Survival Points — NPCs only). PCs can spend Hero Points before the killing blow to convert it to Incapacitated.',
  },
  {
    id: 'chase-pursue-flee-chart',
    title: 'Pursue / Flee maneuvers',
    category: 'rules',
    body: 'During a chase, each side bids an Ease Factor + chooses a maneuver: Pursue / Flee / Force / Quick Turn / Double Back / Trick. The QR of the bidding-side\'s roll shifts the range category (Close / Medium / Long / Distant / Extreme).',
  },
  {
    id: 'fire-combat-modifiers',
    title: 'Fire Combat modifiers',
    category: 'rules',
    body: 'Range bracket (Close / Long), cover, movement, and firing mode (single shot / burst / spray) all adjust the Fire Combat EF. See the in-Roll drawer table for the canonical numbers.',
  },
  {
    id: 'random-encounters',
    title: 'Random encounters',
    category: 'rules',
    body: 'Chapter 14 random-encounter system: GM rolls 2d6 + modifiers on the area_type table (hot = mission-related; cold = wandered astray) to determine if an encounter triggers and which NPC class shows up.',
  },
  {
    id: 'travel-time',
    title: 'Travel time',
    category: 'rules',
    body: 'Travel between canonical cities is computed by mode (train / air / driving 60mph / driving 100mph) and distance. The engine consults a canonical city-pair table; the GM can also rule discretionary travel by prose.',
  },
  {
    id: 'gambling-rules',
    title: 'Gambling (Chapter 7)',
    category: 'rules',
    body: 'Casino games are resolved by Gambling skill: Chemin de Fer, Baccarat, Blackjack, Poker, Roulette. The fan adventures (Diamonds Never Die) introduce Texas Hold\'em via the 4Eyes extension.',
  },
  {
    id: 'fame-recognition',
    title: 'Fame Recognition (Ch 8)',
    category: 'rules',
    body: 'When a PC encounters an NPC, a Perception roll vs the target\'s Fame tier (0-50 / 51-100 / 101-150 / 151+) determines if the NPC recognizes them. Disguise QR applies an EF modifier (+2 / -1 / -3 / -5).',
  },
  {
    id: 'xp-award',
    title: 'XP Award (mission end)',
    category: 'rules',
    body: 'At session end the GM tallies XP based on rank, mission outcome (success / failure / incomplete), and a role-play multiplier 0.5-1.5. XP feeds into rank advancement.',
  },
  {
    id: 'character-creation-validate',
    title: 'Character creation validation',
    category: 'rules',
    body: 'Chapter 2 character creation has hard caps on Skill Points distribution + Characteristic ranges by rank. The dispatcher\'s force_character_creation_validate tool checks a candidate PC against these caps.',
  },
  {
    id: 'repair-attempt',
    title: 'Equipment repair',
    category: 'rules',
    body: 'When equipment takes damage, the PC may attempt repair (Electronics / Science / appropriate skill). d6 damage roll + has_repair_skill determines if repair is possible and EF.',
  },
  {
    id: 'hero-point-spend',
    title: 'Hero Point spend modes',
    category: 'rules',
    body: 'Hero Points spend on: alter_qr (bump a QR up a tier), reroll (re-roll the d100), activate_ability (use a Fields-of-Experience ability), save_pc (convert killing blow to Incapacitated), qr1_award_claim (claim a QR1 narrative beat).',
  },

  // ═══════════════════════════════════════════════════════════════════
  // SKILLS (24 canonical)
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'skill-boating',
    title: 'Boating',
    category: 'skills',
    body: 'Operating watercraft: speedboats, motor yachts, sailboats. Based on DEX + PER. Used in the Tour Eiffel chase Bateau Mouche jump (A View to a Kill) and the Pumping Station boat assault.',
  },
  {
    id: 'skill-charisma',
    title: 'Charisma',
    category: 'skills',
    body: 'Personal magnetism — first impressions, party conversation, charm offensive. Based on WIL. The most-used social skill before Persuasion or Seduction kicks in. WIL + Charisma SL/2 yields Persuasion PC.',
  },
  {
    id: 'skill-cryptography',
    title: 'Cryptography',
    category: 'skills',
    body: 'Code-breaking, cipher decryption, intercepted message analysis. Based on INT + PER. Used heavily by Tibbett, Bond, and Q Branch staff.',
  },
  {
    id: 'skill-demolitions',
    title: 'Demolitions',
    category: 'skills',
    body: 'Setting + disarming explosive charges. Based on INT + DEX. Critical for the Main Strike Mine disarm (¶40 of A View to a Kill) and the Freedom Express munitions sabotage in Code Red.',
  },
  {
    id: 'skill-disguise',
    title: 'Disguise',
    category: 'skills',
    body: 'Visual deception — costume, makeup, prosthetics. Based on INT. Used to penetrate Zorin\'s chateau party as guard impersonators, or the Pentroc facility as scientific researchers.',
  },
  {
    id: 'skill-diving',
    title: 'Diving',
    category: 'skills',
    body: 'Scuba + free-diving operations. Based on STR + PER. The MS Coral submarine sabotage in Diamonds Never Die is a full Diving sequence; the Dr. No island infiltration is shallower Diving.',
  },
  {
    id: 'skill-driving',
    title: 'Driving',
    category: 'skills',
    body: 'Ground vehicles — cars, trucks, motorcycles, snowmobiles. Based on DEX + PER. Bond canonical skill; most chase sequences fall here.',
  },
  {
    id: 'skill-electronics',
    title: 'Electronics',
    category: 'skills',
    body: 'Electronic devices — radios, computers, surveillance gear, micro-chip work. Based on INT + DEX. Zorin and Mortner have absurd Electronics scores (15 / 13).',
  },
  {
    id: 'skill-evasion',
    title: 'Evasion',
    category: 'skills',
    body: 'Defensive movement — dodging, taking cover, breaking line of sight. Based on DEX. Bond\'s second-most-used skill after Driving.',
  },
  {
    id: 'skill-fire-combat',
    title: 'Fire Combat',
    category: 'skills',
    body: 'Ranged weapons — pistols, submachineguns, rifles, snipers. Based on DEX + PER. Resolution uses the Fire Combat table (range bracket → EF), weapon Damage Class for hit effects.',
  },
  {
    id: 'skill-gambling',
    title: 'Gambling',
    category: 'skills',
    body: 'Card + table games. Based on INT + PER. Resolution uses Chapter 7 game-specific rules (Chemin de Fer, Baccarat, etc.). Pola Ivanova and Zorin both have high Gambling.',
  },
  {
    id: 'skill-hand-to-hand-combat',
    title: 'Hand-to-Hand Combat',
    category: 'skills',
    body: 'Unarmed + close-range melee. Based on STR + DEX. Damage Class A/B/C. The Golden Gate Bridge final fight (VG35014 ¶49) is a Hand-to-Hand sequence.',
  },
  {
    id: 'skill-interrogation',
    title: 'Interrogation',
    category: 'skills',
    body: 'Extracting information from a captive (non-violent). Based on WIL + INT. Resolution: PC × EF vs target Willpower with appropriate modifiers.',
  },
  {
    id: 'skill-local-customs',
    title: 'Local Customs',
    category: 'skills',
    body: 'Cultural fluency — knowing how to drink toasts, when to bow, what to wear at a Thai temple. Based on INT + PER. Used heavily in Chiang Rai (Diamonds Never Die).',
  },
  {
    id: 'skill-lockpicking-safecracking',
    title: 'Lockpicking / Safecracking',
    category: 'skills',
    body: 'Mechanical + electronic lock defeat. Based on DEX + PER. Used to crack Komesh\'s Town Hall safe in Diamonds Never Die and Zorin\'s checkbook safe in VG35014.',
  },
  {
    id: 'skill-mountaineering',
    title: 'Mountaineering',
    category: 'skills',
    body: 'Climbing + rope work. Based on STR + DEX. The Golden Gate Bridge mooring-rope climb and the Stacey\'s house safe-window descent (VG35014) are both Mountaineering.',
  },
  {
    id: 'skill-pickpocket',
    title: 'Pickpocket',
    category: 'skills',
    body: 'Sleight of hand — lifting wallets, planting items. Based on DEX. Used to pocket the 1122-marked note in Diamonds Never Die night bazaar.',
  },
  {
    id: 'skill-piloting',
    title: 'Piloting',
    category: 'skills',
    body: 'Aircraft + helicopters. Based on DEX + INT. The Lockheed SR-71A paradrop insertion (VG35014 ¶1) is a Piloting roll for the SR-71 crew; the Polaris helicopter chase tests PC Piloting.',
  },
  {
    id: 'skill-riding',
    title: 'Riding',
    category: 'skills',
    body: 'Horseback + similar mounts. Based on DEX + STR. The chateau steeplechase race (VG35014 ¶25) is a 20-round Riding sequence with obstacles at rounds 3/6/9/12/15/18.',
  },
  {
    id: 'skill-science',
    title: 'Science',
    category: 'skills',
    body: 'Generic STEM — chemistry, physics, biology. Based on INT + PER. Mortner and Zorin both have Science at 14-15.',
  },
  {
    id: 'skill-seduction',
    title: 'Seduction',
    category: 'skills',
    body: 'Romantic + sexual social interaction. Based on WIL + Charisma SL / 2. Five-stage canonical sequence with EFs 10 → 9 → 8 → 5 → 4. Bond\'s canonical specialty.',
  },
  {
    id: 'skill-sixth-sense',
    title: 'Sixth Sense',
    category: 'skills',
    body: 'Intuition — noticing trouble before it lands. Based on PER + WIL. The Aubergene-poisoned-soup tell in VG35014 (¶15) is a Sixth Sense roll.',
  },
  {
    id: 'skill-stealth',
    title: 'Stealth',
    category: 'skills',
    body: 'Moving unseen + unheard. Based on DEX + PER. May Day has Stealth 10/18; trained operatives 8-12.',
  },
  {
    id: 'skill-torture',
    title: 'Torture',
    category: 'skills',
    body: 'Coerced information extraction (violent). Based on WIL. Listed for completeness; Bond PCs use Persuasion / Interrogation instead.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // NPC FIELDS / INTERACTIONS
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'npc-allegiance',
    title: 'NPC allegiance',
    category: 'npc',
    body: 'Each NPC carries an allegiance: enemy, ally, agent_handler, neutral. Affects default Reaction modifier and dispatcher behavior. May Day is enemy; Pola Ivanova starts ally but can drift.',
  },
  {
    id: 'npc-interaction-mods',
    title: 'NPC interaction modifiers',
    category: 'npc',
    body: 'Per-NPC modifiers on Reaction / Persuasion / Seduction / Interrogation / Torture rolls. Zorin: Persuasion -3, Seduction -3. Stacey Sutton: +2 across the board. Cumulative with situational modifiers.',
  },
  {
    id: 'npc-reaction',
    title: 'NPC Reaction',
    category: 'npc',
    body: 'Initial Reaction range from Antagonistic → Hostile → Neutral → Friendly → Helpful. Determines how the NPC responds to first encounter; can shift via successful Persuasion + Seduction.',
  },
  {
    id: 'npc-skills-block',
    title: 'NPC skills block',
    category: 'npc',
    body: 'Each NPC stat sheet shows Skill Level / Primary Chance for every skill they have. Empty skills are unrolled (treated as Primary = characteristic only - 3 EF).',
  },
  {
    id: 'npc-combat-block',
    title: 'NPC combat block',
    category: 'npc',
    body: 'Speed, Hand-to-Hand Damage Class, Stamina hours, Running/Swimming minutes, Carrying capacity, Fame Points, Survival/Hero Points. NPCs use Survival Points where PCs use Hero Points.',
  },
  {
    id: 'npc-weaknesses',
    title: 'NPC weaknesses',
    category: 'npc',
    body: 'Personal vulnerabilities — Greed, Sadism, Acrophobia, Attraction to opposite sex, Dependence on Liquor. The GM may invoke these in narrative beats.',
  },
  {
    id: 'npc-idiosyncrasies',
    title: 'NPC idiosyncrasies',
    category: 'npc',
    body: 'Cosmetic distinguishing features — Zorin has one blue and one grey eye; Scarpine has a scar + nervous tic; Mortner is mild-mannered until cornered. Role-play hooks.',
  },
  {
    id: 'npc-fields-of-experience',
    title: 'Fields of Experience',
    category: 'npc',
    body: 'NPC + PC knowledge domains beyond skills: Biology, Computers, Economics, Fine Arts, Ice Hockey, Law, etc. PC = 20 in any Field they have. Triggers situational +1 / -1 EF bonuses on related rolls.',
  },
  {
    id: 'npc-information-points',
    title: 'NPC information points',
    category: 'npc',
    body: 'Ranked clues an NPC will reveal under interrogation or successful seduction. Higher rank = more sensitive intel. Pola Ivanova has 4 ranked info points in VG35014; Emma Alvarez has 3 in Code Red.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // GM DISPATCHER TOOLS
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'tool-pick-decision',
    title: 'pick_decision',
    category: 'gm-tools',
    body: 'GM dispatcher tool: matches your voice intent to a visible decision button and clicks it. "I take the elevator" → clicks the elevator option.',
  },
  {
    id: 'tool-roll-skill',
    title: 'roll_skill',
    category: 'gm-tools',
    body: 'GM dispatcher tool: fires a skill check with a specified skill + EF. "Sixth Sense check ease 7" → opens the dice cinematic.',
  },
  {
    id: 'tool-describe-npc',
    title: 'describe_npc',
    category: 'gm-tools',
    body: 'GM dispatcher tool: surfaces the NpcCard for a named NPC with relevant context. "Tell me about Korba" → reveals stat block.',
  },
  {
    id: 'tool-look-around',
    title: 'look_around',
    category: 'gm-tools',
    body: 'GM dispatcher tool: describes the current scene with sensory detail. "What do I see" → atmospheric paragraph.',
  },
  {
    id: 'tool-check-inventory',
    title: 'check_inventory',
    category: 'gm-tools',
    body: 'GM dispatcher tool: lists what you\'re carrying. "What do I have" → inventory summary.',
  },
  {
    id: 'tool-recall-briefing',
    title: 'recall_briefing',
    category: 'gm-tools',
    body: 'GM dispatcher tool: surfaces the original mission briefing or key facts. "Why am I here" → re-displays the briefing.',
  },
  {
    id: 'tool-ask-npc',
    title: 'ask_npc',
    category: 'gm-tools',
    body: 'GM dispatcher tool: in-character dialogue with a named NPC. "Talk to Stacey about her father" → simulated NPC response.',
  },
  {
    id: 'tool-force-recognize-npc',
    title: 'force_recognize_npc',
    category: 'gm-tools',
    body: 'GM override tool: forces a TAROT NPC into recognized OR unknown state regardless of normal Fame-recognition mechanics. "Force Korba recognized" / "Force Skorpios unknown".',
  },
  {
    id: 'tool-force-dice-roll',
    title: 'force_dice_roll',
    category: 'gm-tools',
    body: 'GM dispatcher tool: raw dice notation roll without a skill ("roll 2d6 damage", "roll a d20", "roll percentile"). Mode is parsed from the verb.',
  },
  {
    id: 'tool-force-chase-resolve',
    title: 'force_chase_resolve',
    category: 'gm-tools',
    body: 'GM dispatcher tool: resolves one round of an active chase with a chosen maneuver (pursue / flee / force / quick_turn / double_back / trick) and EF bid.',
  },
  {
    id: 'tool-force-persuasion-attempt',
    title: 'force_persuasion_attempt',
    category: 'gm-tools',
    body: 'GM dispatcher tool: Persuasion roll vs a specific NPC with explicit modifiers (trivial / minor / major / deadly request + appropriate_emotion bonus). Uses the Charisma SKILL roll\'s QR to look up the Ch 6 Persuasion CHART.',
  },
  {
    id: 'tool-force-seduction-stage-advance',
    title: 'force_seduction_stage_advance',
    category: 'gm-tools',
    body: 'GM dispatcher tool: advance the 5-stage Seduction sequence with one NPC. Tracks current_stage 0-5; canonical EFs 10/9/8/5/4. Includes appearance + situational modifiers.',
  },
  {
    id: 'tool-force-fame-recognition',
    title: 'force_fame_recognition',
    category: 'gm-tools',
    body: 'GM dispatcher tool: forces a canonical Ch 8 Fame-recognition check. Tier-based: target Fame 0-50 / 51-100 / 101-150 / 151+. Optional disguise_qr applies +2 / -1 / -3 / -5 EF modifier.',
  },
  {
    id: 'tool-force-hero-point-spend',
    title: 'force_hero_point_spend',
    category: 'gm-tools',
    body: 'GM dispatcher tool: explicit Hero Point spend with purpose enum (alter_qr / reroll / activate_ability / save_pc / qr1_award_claim) + points_spent + current_balance.',
  },
  {
    id: 'tool-force-npc-encounter-roll',
    title: 'force_npc_encounter_roll',
    category: 'gm-tools',
    body: 'GM dispatcher tool: Chapter 14 random-encounter roll. area_type {hot, cold} + optional 2d6 modifier.',
  },
  {
    id: 'tool-force-travel-time-compute',
    title: 'force_travel_time_compute',
    category: 'gm-tools',
    body: 'GM dispatcher tool: computes canonical travel time between two cities given mode (train / air / driving_60mph / driving_100mph). Pulls from the canonical city-pair matrix.',
  },
  {
    id: 'tool-force-xp-award',
    title: 'force_xp_award',
    category: 'gm-tools',
    body: 'GM dispatcher tool: end-of-mission XP tally. Rank + outcome (success / failure / incomplete) + role_play_multiplier 0.5-1.5.',
  },
  {
    id: 'tool-force-gambling-play',
    title: 'force_gambling_play',
    category: 'gm-tools',
    body: 'GM dispatcher tool: place a bet at a Ch 7 casino game. game_type {chemin_de_fer, baccarat, blackjack, poker, roulette} + bet + cheat flag.',
  },
  {
    id: 'tool-force-repair-attempt',
    title: 'force_repair_attempt',
    category: 'gm-tools',
    body: 'GM dispatcher tool: equipment repair under damage. d6 damage roll + equipment_type + has_repair_skill.',
  },
  {
    id: 'tool-force-character-creation-validate',
    title: 'force_character_creation_validate',
    category: 'gm-tools',
    body: 'GM dispatcher tool: validate a Ch 2 character-creation candidate against canonical rank caps + characteristic ranges. Returns valid + list of violations.',
  },

  // ═══════════════════════════════════════════════════════════════════
  // WEAPON ATTRIBUTE GLOSSARY (Q Manual VG35002 Appendix A — Phase 1D 2026-06-08)
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'weapon-attr-pm',
    title: 'PM — Performance Modifier',
    category: 'rules',
    body: 'The Ease Factor modifier applied when the weapon is fired. Positive values make the shot easier, negative make it harder.\n\nSource: Q Manual Appendix A, page 131.',
  },
  {
    id: 'weapon-attr-shots-per-round',
    title: 'S/R — Shots per Round',
    category: 'rules',
    body: "The maximum number of bullets that can be fired within one Action Round.\n\nWhen two numbers are listed (e.g. AKM, AK-47, M-16, VP-70z), the first is used when firing single shots, the second when firing automatic bursts. Unless otherwise noted, only one trigger pull is allowed per Round on automatic weapons. If an automatic weapon isn't rated for single shots, it can't be used that way.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'weapon-attr-ammo',
    title: 'AMMO — Ammunition',
    category: 'rules',
    body: 'The number of bullets in one complete load. After firing this many bullets, the character must reload.\n\nSource: Q Manual Appendix A, page 131.',
  },
  {
    id: 'weapon-attr-damage-class',
    title: 'DC — Damage Class',
    category: 'rules',
    body: "The stopping power of the weapon. The higher the letter in the alphabet, the more powerful the projectile (D < E < F < G < H < I < J < K < L).\n\nSome weapons list two Damage Classes: the first is for single shots or spray fire on automatic; the second is for automatic fire at a single target. Hand-to-Hand Weapons list a number instead of a letter — it improves the wielder's Hand-to-Hand Damage Class by that number of classes.\n\nThe `3×L` notation (M-2 Browning) means one wound inflicts THREE wounds of that level when fired at a single individual.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'weapon-attr-close',
    title: 'CLOSE — Close Range',
    category: 'rules',
    body: 'The numbers on the stat block are in inches for graph-paper resolution; multiply by 10 to get distance in feet.\n\nA weapon fired at Close range gets a positive Ease Factor modifier AND its Damage Class is improved by one Class.\n\nSource: Q Manual Appendix A, page 131.',
  },
  {
    id: 'weapon-attr-long',
    title: 'LONG — Long Range',
    category: 'rules',
    body: "Ranges in inches (×10 for feet). A weapon fired at Long range gets a negative Ease Factor modifier AND its Damage Class is lowered by one Class.\n\nThe range BETWEEN Long and Close is the neutral band — no EF modifier.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'weapon-attr-concealment',
    title: 'CON — Concealment',
    category: 'rules',
    body: "Determines how easily the weapon can be hidden from an observer. The number is the Ease Factor modifier applied to any Perception roll made by someone trying to spot it.\n\nBecause the modifier is applied to the PERCEIVER, a NEGATIVE CON benefits the carrier (harder to spot) and a POSITIVE CON benefits the perceiver.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'weapon-attr-jam',
    title: 'JAM — Jam Rating',
    category: 'rules',
    body: "If a d100 firing roll falls within the indicated Jam rating, the weapon is jammed and can't fire until cleared.\n\nTo unjam: make an Ease Factor 5 Fire Combat roll at the start of each Action Round before Declarations. A Jam supersedes any QR result.\n\n`na` means 'not applicable' — common on revolvers + manual-action weapons that mechanically can't jam.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'weapon-attr-draw',
    title: 'DRAW — Draw Adjustment',
    category: 'rules',
    body: 'The number applied to the die roll during a "draw" situation. A positive value improves the chance of shooting first; a negative value lessens it.\n\nSource: Q Manual Appendix A, page 131.',
  },
  {
    id: 'weapon-attr-reload',
    title: 'RL — Reload',
    category: 'rules',
    body: "The number of Action Rounds it takes to reload the weapon.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'weapon-attr-cost',
    title: 'COST — Cost (£ sterling)',
    category: 'rules',
    body: "The expense in Pounds sterling to buy the weapon while on a mission. Currency is 1983 GBP.\n\nThis price has NOTHING to do with Experience Point costs for borrowing weapons from Q Branch — those are a separate mechanic.\n\nSource: Q Manual Appendix A, page 131.",
  },

  // ═══════════════════════════════════════════════════════════════════
  // VEHICLE ATTRIBUTE GLOSSARY (Q Manual VG35002 Appendix A — Phase 2D 2026-06-08)
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'vehicle-attr-pm',
    title: 'PM — Performance Modifier',
    category: 'rules',
    body: 'The Ease Factor modifier applied when attempting maneuvers AND when making the Safety roll for mishaps. Positive values help; negative values hurt.\n\nDistinct from weapon PM (which applies to firing). Vehicle PM is the central handling-quality stat.\n\nSource: Q Manual Appendix A, page 131.',
  },
  {
    id: 'vehicle-attr-red',
    title: 'RED — Redline',
    category: 'rules',
    body: "The vehicle's ability to sustain continuous high-performance operation. RED is the LOWEST Ease Factor the operator may bid WITHOUT incurring an automatic mishap roll.\n\nWhen the operator bids lower than RED, an automatic mishap roll fires at the end of the Action Round — in ADDITION to any other mishap roll already triggered. Pushing past RED is a deliberate gambit, not a free option.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'vehicle-attr-crus',
    title: 'CRUS — Cruising Speed',
    category: 'rules',
    body: "The vehicle's cruising speed in miles per hour. The normal sustained speed during long-range operation; distinct from MAX (peak speed for short bursts).\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'vehicle-attr-max',
    title: 'MAX — Maximum Speed',
    category: 'rules',
    body: "The vehicle's maximum speed in miles per hour. Peak attainable speed; unsustainable for long durations and typically requires bidding below RED to reach.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'vehicle-attr-rge',
    title: 'RGE — Range',
    category: 'rules',
    body: "Average range in statute miles the vehicle may travel on one full load of fuel.\n\nImportant only when the character operating the vehicle is OUTSIDE a city or other location where fuel is plentiful. In urban operations RGE is rarely the limiting factor; in wilderness or open-water pursuit it can become the central constraint.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'vehicle-attr-fce',
    title: 'FCE — Force Rating',
    category: 'rules',
    body: "Vehicle's overall mass + structural authority during Force maneuvers (ramming, pushing, vehicle-vs-vehicle contact).\n\nApplied to the Ease Factor for the Force maneuver. If the vehicle is DOING the Forcing, the modifier is ADDED to the EF; if the vehicle is being FORCED, the number is SUBTRACTED.\n\nNo Ease Factor can be above 10 or below ½ — FCE differentials get clamped at the extremes.\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'vehicle-attr-str',
    title: 'STR — Structure Points',
    category: 'rules',
    body: "Used to determine the number of modifications that can be added to the vehicle. Each modification requires a certain number of Structure Points; a vehicle can have a number of additional modifications equal to its number of Structure Points.\n\nThe core mod-budget rule: total STR points consumed by modifications must NOT exceed the vehicle's STR. A Lotus Esprit at STR 5 = max 5 mod-Points of additions.\n\nMods without an explicit Structure Point cost default to half a Structure Point (Q Manual page 83 rule-of-thumb).\n\nSource: Q Manual Appendix A, page 131.",
  },
  {
    id: 'vehicle-attr-cost',
    title: 'COST — Cost (£ sterling)',
    category: 'rules',
    body: "Amount in Pounds sterling a character would spend purchasing the vehicle in the course of an adventure. Currency is 1983 GBP.\n\nNotation:\n  • `k` = thousands of Pounds (e.g. 600k = £600,000)\n  • `m` = millions of Pounds (e.g. 8.1m = £8,100,000)\n\nThis COST does NOT apply to Experience Points used to borrow equipment from Q Branch — those are a separate mechanic.\n\nSource: Q Manual Appendix A, page 131.",
  },
];

const BY_ID: Map<string, HelpItem> = new Map(ITEMS.map((i) => [i.id, i]));

export function getHelpItem(id: string): HelpItem | null {
  return BY_ID.get(id) ?? null;
}

export function getAllHelpItems(): HelpItem[] {
  return [...ITEMS];
}

export function getHelpItemsByCategory(): Record<string, HelpItem[]> {
  const groups: Record<string, HelpItem[]> = {};
  for (const item of ITEMS) {
    const key = item.category ?? 'ui';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return groups;
}
